import axios from 'axios';

/**
 * BACKEND_URL — resolvido com fallback em cadeia para robustez em produção:
 * 1. REACT_APP_BACKEND_URL (env, ideal)
 * 2. Se a env aponta para um domínio de terceiro diferente do atual E o navegador está rodando o app,
 *    ignora a env (evita CORS quebrado quando o deploy pegou uma env stale) e usa a origem atual.
 * 3. window.location.origin — sempre funciona porque o ingress do Emergent roteia /api/* para o backend.
 */
function resolveBackendUrl() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return fromEnv || '';
  const origin = window.location.origin;
  if (!fromEnv) return origin;
  try {
    const envHost = new URL(fromEnv).host;
    const currentHost = window.location.host;
    // Preview local: env aponta pra domínio preview e página está lá → usa env
    // Prod local: env aponta pra prod e página está lá → usa env
    // Produção com env stale (aponta pra outro domínio): usa origin como fallback seguro
    if (envHost === currentHost) return fromEnv;
    // Se está em preview.emergentagent.com E env também é preview.emergentagent.com de outro slug,
    // ainda melhor usar origin (evita CORS entre subdomínios preview)
    return origin;
  } catch {
    return origin;
  }
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Token storage keys
const TOKEN_KEY = 'craftflow_access_token';
const USER_KEY = 'marcenaria_user';

// Token management functions
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const setStoredUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const removeStoredUser = () => {
  localStorage.removeItem(USER_KEY);
};

export const clearAuth = () => {
  removeToken();
  removeStoredUser();
};

// ============================================
// GLOBAL AXIOS INTERCEPTORS (for legacy code)
// ============================================
// Default 60s — operações normais terminam em <2s, mas dá folga pra chamadas
// mais pesadas (upload de imagens, polling de render IA, etc).
// Chamadas que precisam ainda mais tempo passam timeout explícito.
axios.defaults.timeout = 60000;

axios.interceptors.request.use(
  (config) => {
    // 🛡️ Guarda anti-URL-stale: se o request estiver indo pra um domínio DIFERENTE
    // do domínio atual, redireciona pra origem atual. Isso evita bugs de env var
    // desatualizada no deploy (ex: REACT_APP_BACKEND_URL apontando pra domínio antigo).
    try {
      const url = config.url || '';
      if (typeof window !== 'undefined' && (url.startsWith('http://') || url.startsWith('https://'))) {
        const target = new URL(url);
        if (target.host !== window.location.host) {
          const isApiCall = target.pathname.startsWith('/api/');
          const isEmergentInfra = target.host.includes('emergent') || target.host.includes('cloudfront') || target.host.includes('stripe') || target.host.includes('posthog');
          if (isApiCall && !isEmergentInfra) {
            config.url = `${window.location.origin}${target.pathname}${target.search}${target.hash}`;
          }
        }
      }
    } catch (e) { /* noop */ }

    const token = getToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If 401, clear auth and dispatch logout event
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // 🛡️ Ignora 401 de rotas públicas / não-JWT — não desloga o admin por causa disso.
      // Ex: /factory/workers/auth (PIN de funcionário), /site/config, /auth/login, /ponto/*.
      // Essas rotas usam PIN ou são públicas — um 401 aqui NÃO significa que o admin
      // perdeu a sessão. Sem esse guard, o Chão de Fábrica derruba a sessão do admin
      // quando o PIN salvo está errado, causando loop de volta pro início.
      const url = originalRequest?.url || '';
      const isNonJwt401 = /\/factory\/workers\/auth|\/site\/(config|image-file|reset|suggest|upload-image)|\/auth\/login|\/auth\/register|\/auth\/forgot|\/ponto\//.test(url);
      if (isNonJwt401) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        // Try to refresh token
        const token = getToken();
        if (token) {
          try {
            const response = await axios.post(`${API}/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${token}` },
              _retry: true
            });
            if (response.data.access_token) {
              setToken(response.data.access_token);
              originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
              return axios(originalRequest);
            }
          } catch {
            // Sessão realmente expirou — desloga.
            clearAuth();
            window.dispatchEvent(new CustomEvent('auth:logout'));
          }
        }
        // Sem token → o admin nunca esteve logado (ex: funcionário no Chão de Fábrica
        // acessando endpoint protegido). NÃO dispara auth:logout — isso derrubaria
        // o app pra landing e criaria o "entra e sai" mesmo sem sessão pra perder.
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// API CLIENT (recommended for new code)
// ============================================
// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // 🛡️ Ignora 401 de rotas públicas / não-JWT — não desloga o admin por causa disso.
    const _url = originalRequest?.url || '';
    if (error.response?.status === 401 &&
        /\/factory\/workers\/auth|\/site\/(config|image-file|reset|suggest|upload-image)|\/auth\/login|\/auth\/register|\/auth\/forgot|\/ponto\//.test(_url)) {
      return Promise.reject(error);
    }

    // If 401 error and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      const token = getToken();
      if (token) {
        try {
          const response = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.access_token) {
            setToken(response.data.access_token);
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect to login
          console.log('Token refresh failed, clearing session');
          clearAuth();
          // Dispatch custom event for App.js to handle
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return Promise.reject(refreshError);
        }
      } else {
        // Sem token → nunca esteve logado. NÃO dispara auth:logout (evita bounce
        // pra landing no Chão de Fábrica quando worker acessa endpoint protegido).
      }
    }
    
    return Promise.reject(error);
  }
);

// Verify token validity
export const verifyToken = async () => {
  const token = getToken();
  if (!token) return false;
  
  try {
    const response = await apiClient.post('/auth/verify');
    return response.data.valid;
  } catch {
    return false;
  }
};

// Get current user from API (refreshes user data)
export const fetchCurrentUser = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    if (response.data.success && response.data.user) {
      setStoredUser(response.data.user);
      return response.data.user;
    }
    return null;
  } catch {
    return null;
  }
};

export default apiClient;
